import {
	FETCH_CART, CART_ERROR, RESET_REMOVE_COUNT, SET_DELIVERY_FREQUENCY, SELECT_MIN_BOX,
	TOGGLE_CHECKOUT_LOADING, SELECT_BOX
} from "utils/action-types";
import {
	activeBoxKey, frequencyKey, freeGiftRemovedKey, CART_ID, CHECKOUT_DOMAIN, littledataPixelIds
} from "utils/constants";
import { findClosestBox, getExistingCartId, getOrCreateCartId, mapCart } from "utils/cart";
import { trackBeginCheckout } from "services/analytics";
import checkoutQueryString from "helpers/checkout-query-string";
import littledata from "@littledata/headless-shopify-sdk";
import * as CookieConsent from "vanilla-cookieconsent";
import extractIdFromGid from "helpers/extract-gid";

const checkForCartBonus = (cart) => async (dispatch, getState) => {
	const { cartBonus, isOneTimeCollection } = getState().global;
	if (!cartBonus) return;

	const freeGiftRemoved = sessionStorage.getItem(freeGiftRemovedKey);
	if (freeGiftRemoved === cartBonus.product.variant.id)
		return;

	const { id, stock } = cartBonus.product.variant;
	const cartItem = cart.items[id];
	const meetsThreshold = cart.totalProgress >= cartBonus.weightThreshold;

	if (cartItem) {
		if (!meetsThreshold || isOneTimeCollection) dispatch(removeCartItem(cartItem.tempId));
		// Ensure the free gift is only added once
		if (cartItem.quantity > 1) dispatch(updateCartItem(cartItem.tempId, 1));
	} else if (meetsThreshold && !isOneTimeCollection) {
		await dispatch(addCartItem(id, stock === "one-time"));
	}
};

const updateCart = (cart) => async (dispatch) => {
	if (!cart?.id) return;

	const updatedCart = mapCart(cart);

	dispatch({ type: FETCH_CART, payload: updatedCart });
	await dispatch(checkForCartBonus(updatedCart));

	return updatedCart;
};


const fetchCart = (discountCode) => async (dispatch, getState) => {
	const { shopifyClient } = getState().global;
	const cartId = getExistingCartId();
	let cart;

	if (cartId)
		cart = await shopifyClient.cart.fetch(cartId);

	if (!cart?.id) {
		localStorage.removeItem(CART_ID);
		localStorage.removeItem("littledataIDs");
		const input = {};
		if (discountCode) input.discountCodes = [discountCode];
		cart = await shopifyClient.cart.create(input);
		localStorage.setItem(CART_ID, cart.id);
	}

	const dispatchedCart = await dispatch(updateCart(cart));

	dispatch(addLittledataAttributes());

	return dispatchedCart;
};

export const addLittledataAttributes = (cart) => async (_, getState) => {
	const { acceptedCategories } = CookieConsent.getUserPreferences();
	if (!acceptedCategories.includes("analytics_storage"))
		return;

	const { shopifyClient } = getState().global;
	const { id: cartId, attributes } = cart || getState().cart;

	if (attributes.find(attr => attr.key === "_fbp-clientID")) {
		return;
	}

	try {
		const littledataNoteAttributes = await littledata.fetchClientIds(littledataPixelIds) || [];
		const pureCartId = extractIdFromGid(cartId).split("?")[0];
		await Promise.all([
			littledata.sendCartToLittledata(pureCartId),
			shopifyClient.cart.updateAttributes(cartId, [...littledataNoteAttributes, { key: "cartID", value: pureCartId }])
		]);

	} catch (error) {
		console.error("An error occurred while adding cart attributes:", error);
		return [];
	}
};

const subscribe = (items, sellingPlanId, subscriptionDiscount, cartDiscounts) => async (_, getState) => {
	const { shopifyClient } = getState().global;
	const cartId = getExistingCartId();

	const lines = items.map((item) => {
		const sellingPlanAllocation = item.variant.sellingPlanAllocations
			?.find(({ sellingPlan }) => extractIdFromGid(sellingPlan.id) === sellingPlanId);

		return {
			id: item.tempId,
			quantity: item.quantity,
			sellingPlanId: sellingPlanAllocation?.sellingPlan?.id
		};
	});

	await shopifyClient.cart.updateLineItems(cartId, lines);
	const discountExist = cartDiscounts.find(({ code }) => code === subscriptionDiscount);
	// Check if the subscription discount code is already applied to the cart, if not, apply it
	if (!discountExist)
		await shopifyClient.cart.updateDiscountCodes(cartId, [...cartDiscounts, subscriptionDiscount]);
};

const placeOneTimeOrder = (items) => async (_, getState) => {
	const isRecurringOrder = items.some(({ sellingPlan }) => sellingPlan);
	if (!isRecurringOrder) return;

	// If the cart contains subscription items, remove the selling plan 
	const lines = items.map((item) => ({
		id: item.tempId,
		quantity: item.quantity,
		sellingPlanId: null
	}));

	const cartId = getExistingCartId();
	const { shopifyClient } = getState().global;
	await shopifyClient.cart.updateLineItems(cartId, lines);
};

export const beginCheckout = (language, isOneTimePurchase, subscriptionDiscount, customSellingPlanId) =>
	async (dispatch, getState) => {
		const cart = getState().cart;
		const { authenticated, email, defaultAddress } = getState().account.profile;

		dispatch({ type: TOGGLE_CHECKOUT_LOADING });
		const queryString = authenticated ? `&${checkoutQueryString(email, defaultAddress)}` : "";

		const items = Object.values(cart.items);
		let url = cart.checkoutUrl + "&locale=" + language + queryString;
		const sellingPlanId = customSellingPlanId || cart.delivery?.value;

		const redirectedDiscount = cart.discountCodes.find(code => code !== subscriptionDiscount) || subscriptionDiscount;

		if (isOneTimePurchase)
			await dispatch(placeOneTimeOrder(items));
		else
			await dispatch(subscribe(items, sellingPlanId, redirectedDiscount, cart.discountCodes));

		//tracking
		trackBeginCheckout(cart, isOneTimePurchase, isOneTimePurchase ? redirectedDiscount : "");

		const parseUrl = (url) => {
			try { return new URL(url); } catch (_) { return false; }
		};

		const checkoutUrl = parseUrl(url);

		if (checkoutUrl) window.location.href = url.replace(checkoutUrl.hostname, CHECKOUT_DOMAIN);

		setTimeout(() => {
			dispatch({ type: TOGGLE_CHECKOUT_LOADING });
		}, 3000);
	};

const addCartItem = (merchandiseId, oneTime = false) => async (dispatch, getState) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const lineItems = [{ merchandiseId, quantity: 1, attributes: oneTime ? [{ key: "_oneTime", value: "true" }] : [] }];

		const updatedCart = await shopifyClient.cart.addLineItems(cartId, lineItems);
		return dispatch(updateCart(updatedCart));
	} catch (err) {
		dispatch({ type: CART_ERROR });
		dispatch(fetchCart());
	}
};

const addCartItems = (variants) => async (dispatch, getState) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);

		const lineItems = variants.map((v) => ({
			merchandiseId: v?.id,
			quantity: v?.quantity ?? 1,
			attributes: v?.stock === "one-time" ? [{ key: "_oneTime", value: "true" }] : []
		}));
		const updatedCart = await shopifyClient.cart.addLineItems(cartId, lineItems);
		return dispatch(updateCart(updatedCart));
	} catch (err) {
		dispatch({ type: CART_ERROR });
		dispatch(fetchCart());
		return { error: true };
	}
};

const updateCartItem = (id, quantity) => async (dispatch, getState) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const lineItems = [{ id, quantity }];
		const updatedCart = await shopifyClient.cart.updateLineItems(cartId, lineItems);
		return dispatch(updateCart(updatedCart));
	} catch (err) {
		dispatch({ type: CART_ERROR });
		dispatch(fetchCart());
	}
};

const removeCartItem = (id) => async (dispatch, getState) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const updatedCart = await shopifyClient.cart.removeLineItems(cartId, [id]);
		return dispatch(updateCart(updatedCart));
	} catch (err) {
		dispatch({ type: CART_ERROR });
		dispatch(fetchCart());
	}
};

const removeCartItems = (lineIds) => async (dispatch, getState) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const updatedCart = await shopifyClient.cart.removeLineItems(cartId, lineIds);
		if (lineIds.length >= 2) dispatch({ type: RESET_REMOVE_COUNT });
		return dispatch(updateCart(updatedCart));
	} catch (err) {
		dispatch({ type: CART_ERROR });
		dispatch(fetchCart());
	}
};

const addDiscountCode = (code) => async (dispatch, getState) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const { discountCodes } = getState().cart;
		const codes = [code, ...discountCodes];
		const updatedCart = await shopifyClient.cart.updateDiscountCodes(cartId, codes);
		return dispatch(updateCart(updatedCart));
	} catch (err) {
		dispatch({ type: CART_ERROR });
		dispatch(fetchCart());
	}
};

// Utility function to select and save a box
const selectAndSaveBox = (box) => async (dispatch) => {
	dispatch({ type: SELECT_BOX, box });
	localStorage.setItem(activeBoxKey, JSON.stringify(box));
};

export const selectAndSaveFrequency = (frequency, frequencies = []) => async (dispatch) => {
	// Find the frequency payload from the list of frequencies because the language might have changed
	const frequencyPayload = frequencies.length ? frequencies.find(f =>
		f.interval === frequency.interval && f.intervalCount === frequency.intervalCount) : frequency;
	dispatch({ type: SET_DELIVERY_FREQUENCY, payload: frequencyPayload });
	localStorage.setItem(frequencyKey, JSON.stringify(frequencyPayload));
};

const updateActiveBox = (boxes, frequencies, allowDowngrade = false) => async (dispatch, state) => {
	const { activeBox, totalProgress } = state().cart;

	const minBox = findClosestBox(boxes, totalProgress);
	dispatch({ type: SELECT_MIN_BOX, box: minBox });

	// If the active box is selected and the total progress is less than the box size, select the minimum box.
	// Downgrade is allowed only if the allowDowngrade flag is set to true.
	if (allowDowngrade && activeBox?.size.value > minBox.size.value)
		return dispatch(selectAndSaveBox(minBox));

	const savedActiveBox = JSON.parse(localStorage.getItem(activeBoxKey));
	const savedFrequency = JSON.parse(localStorage.getItem(frequencyKey)) || {};
	const defaultBox = boxes.find(box => box.size.value === 16);
	const defaultFreq = frequencies.find(f => f.interval === "WEEK" && f.intervalCount === 4) || frequencies[0];

	// If no active box is selected and no saved box exists, select the default box
	if (!savedActiveBox && totalProgress <= defaultBox.size.value) {
		dispatch(selectAndSaveBox(defaultBox));
		dispatch(selectAndSaveFrequency(defaultFreq, frequencies));
	}

	// If no active box is selected, select the saved box from localStorage if it exists
	if (!activeBox && savedActiveBox?.price) {
		const frequency = (savedActiveBox.size.value % savedFrequency.intervalCount === 0) ? savedFrequency : defaultFreq;
		dispatch(selectAndSaveFrequency(frequency, frequencies));
		dispatch(selectAndSaveBox(savedActiveBox));
	}

	// // If an active box is selected, update to the minimum box if it exceeds the current box size
	if (minBox.size.value > activeBox?.size.value)
		dispatch(selectAndSaveBox(minBox));
};

export {
	fetchCart, addCartItem, addCartItems, updateCartItem, removeCartItem, removeCartItems,
	addDiscountCode, selectAndSaveBox, updateActiveBox
};